/* eslint-disable @typescript-eslint/member-ordering */
import { error, _decorator, Enum, CCInteger, ccenum, dragonBones, Component } from 'cc';
import { DEV, EDITOR } from 'cc/env';
import BaseAction from './BaseAction';
import { setEnumAttr, hasSameEnumEntries, withNoneOption } from '../../Utils/Utility';

const { ccclass, property } = _decorator;

export enum DefaultArmaturesEnum {
    '&lt;None&gt;' = 0,
}
ccenum(DefaultArmaturesEnum);
export enum DefaultAnimsEnum {
    '<None>' = 0,
}
ccenum(DefaultAnimsEnum);

// 操作行為
export enum DragonBoneAnimationActionType {
    PlayAnimation,
    FadeInAnimation,
    StopAnimation,
    ResetAnimation,
    /** no actual method will be invoked, just used to prevent other actions from being executed */
    SetProperty,
}

@ccclass('DragonBoneAnimationAction')
export default class DragonBoneAnimationAction extends BaseAction {
    @property({ type: dragonBones?.ArmatureDisplay ?? Component, displayName: DEV && 'DragonBone元件' })
    public get displayArmature() {
        return this.armatureDisplay;
    }
    public set displayArmature(value: dragonBones.ArmatureDisplay) {
        this.armatureDisplay = value;
        this._refreshInspector();
    }

    @property({ type: Enum(DragonBoneAnimationActionType), displayName: DEV && '表演類型', tooltip: DEV && '操作行為(actionType)' })
    public readonly actionType: DragonBoneAnimationActionType = DragonBoneAnimationActionType.PlayAnimation;

    @property({
        displayName: DEV && '動畫名稱',
        tooltip: DEV && '動畫名稱(animationName)',
        type: DefaultAnimsEnum,
        visible(this: DragonBoneAnimationAction) {
            return (
                !!this.armatureDisplay?.dragonAsset &&
                (this.actionType === DragonBoneAnimationActionType.PlayAnimation || this.actionType === DragonBoneAnimationActionType.FadeInAnimation)
            );
        },
    })
    public get _animationIndex() {
        if (EDITOR) {
            this._syncInspectorEnums();
        }
        const { animationName } = this;
        if (this.armatureDisplay?.dragonAsset) {
            if (animationName) {
                const animsEnum = this.armatureDisplay.dragonAsset.getAnimsEnum(this.armatureDisplay.armatureName);
                if (!animsEnum) {
                    error(`無法從節點 {${this.armatureDisplay.node.getPathInHierarchy()}} 的 dragonAsset 獲取 animsEnum，請檢查 dragonAsset 是否丟失。`);
                    return 0;
                }
                const updatedAnimsEnum: { [key: string]: number } = {};

                 
                for (const [key, value] of Object.entries(animsEnum)) {
                    updatedAnimsEnum[key.split('(時長:')[0].slice(0, -1)] = value;
                }
                if (updatedAnimsEnum) {
                    const animIndex = updatedAnimsEnum[animationName];
                    if (animIndex !== undefined) {
                        return animIndex;
                    }
                }
            } else {
                this._refreshInspector();
            }
        }
        return 0;
    }
    public set _animationIndex(value: number) {
        let animsEnum = {};
        if (this.armatureDisplay?.dragonAsset) {
            animsEnum = this.armatureDisplay.dragonAsset.getAnimsEnum(this.armatureDisplay.armatureName);
        }
        // console.log(animsEnum, value)
        const animName = animsEnum[value];
        if (animName !== undefined) {
            // console.log(animName)
            this.animationName = animName.split('(時長:')[0].slice(0, -1);
            this._refreshInspector();
        }
    }

    @property({
        displayName: DEV && '動畫名稱',
        tooltip: DEV && '動畫名稱(animationName)',
        visible(this: DragonBoneAnimationAction) {
            return (
                !this.armatureDisplay?.dragonAsset &&
                (this.actionType === DragonBoneAnimationActionType.PlayAnimation || this.actionType === DragonBoneAnimationActionType.FadeInAnimation)
            );
        },
        serializable: true,
    })
    public animationName: string = '';

    @property({
        type: CCInteger,
        displayName: DEV && '起始時間',
        tooltip: DEV && '動畫直接從多少毫秒開始 \n(預設:0, 接續動畫不適用)',
        visible(this: DragonBoneAnimationAction) {
            return this.actionType === DragonBoneAnimationActionType.PlayAnimation;
        },
    })
    public readonly startTime: number = 0;

    @property({
        displayName: DEV && '播放次數',
        tooltip: DEV && '指定動畫播放次數\n(-1: 設定檔內的預設次數、 0: 無限循環, 預設: -1)',
        visible(this: DragonBoneAnimationAction) {
            return this.actionType === DragonBoneAnimationActionType.PlayAnimation || this.actionType === DragonBoneAnimationActionType.FadeInAnimation;
        },
    })
    public readonly playTimes: number = -1;

    @property({
        displayName: DEV && '淡入時長',
        tooltip: DEV && '淡入時長(毫秒)\n(預設:0)',
        visible(this: DragonBoneAnimationAction) {
            return this.actionType === DragonBoneAnimationActionType.FadeInAnimation;
        },
    })
    public readonly fadeInTime: number = 0;

    @property({
        displayName: DEV && '是否變更Armature',
        tooltip: DEV && '是否變更 DragonBone Armature',
        visible(this: DragonBoneAnimationAction) {
            return true;
        },
    })
    public readonly changeArmature: boolean = false;

    @property({
        displayName: DEV && 'Armature名稱',
        tooltip: DEV && 'DragonBone 的 Armature名稱',
        type: DefaultArmaturesEnum,
        visible(this: DragonBoneAnimationAction) {
            return !!this.armatureDisplay?.dragonAsset && this.changeArmature;
        },
    })
    public get _armatureIndex() {
        if (EDITOR) {
            this._syncInspectorEnums();
        }
        const { armatureName } = this;
        if (this.armatureDisplay?.dragonAsset) {
            if (armatureName) {
                const armaturesEnum = this._getArmatureEnumSource();
                if (armaturesEnum) {
                    const armatureIndex = armaturesEnum[armatureName];
                    if (Number.isInteger(armatureIndex)) {
                        return armatureIndex;
                    }
                }
            }
            this._refreshInspector();
        }
        return 0;
    }
    public set _armatureIndex(value: number) {
        if (value === 0) {
            this.armatureName = '';
        } else {
            const armature = this._getArmatureEnumSource()?.[value];
            if (armature !== undefined) {
                this.armatureName = armature;
            }
        }
        this._refreshInspector();
    }

    @property({
        displayName: DEV && 'Armature名稱',
        tooltip: DEV && 'DragonBone 的 Armature名稱',
        serializable: true,
        visible(this: DragonBoneAnimationAction) {
            return !this.armatureDisplay?.dragonAsset && this.changeArmature;
        },
    })
    public armatureName: string = '';

    @property({
        displayName: DEV && '是否設置加速倍率',
        tooltip: DEV && '是否設置加速倍率',
    })
    public readonly setTimeScale: boolean = false;

    @property({
        displayName: DEV && '加速倍率',
        tooltip: DEV && '更改原動畫加速倍率\n',
        visible(this: DragonBoneAnimationAction) {
            return this.setTimeScale;
        },
    })
    public readonly timeScale: number = 1;

    @property({
        visible: false,
        serializable: true,
    })
    public armatureDisplay: dragonBones.ArmatureDisplay = null;

    protected _enumArmatures: any = Enum({});
    protected _enumAnimations: any = Enum({});

    private _refreshInspector() {
        this._updateArmatureEnum();
        this._updateAnimEnum();
    }

    private _getAnimEnumSource() {
        if (this.armatureDisplay?.dragonAsset) {
            return this.armatureDisplay.dragonAsset.getAnimsEnum(this.armatureDisplay.armatureName);
        }
        return DefaultAnimsEnum;
    }

    private _updateAnimEnum() {
        const animEnum = this._getAnimEnumSource();
        this._enumAnimations = Enum({});
        Object.assign(this._enumAnimations, animEnum);
        Enum.update(this._enumAnimations);
        setEnumAttr(this, '_animationIndex', this._enumAnimations);
    }

    private _getArmatureEnumSource() {
        if (this.armatureDisplay?.dragonAsset) {
            return withNoneOption(this.armatureDisplay.dragonAsset.getArmatureEnum());
        }
        return DefaultArmaturesEnum;
    }

    // update armature list for editor
    private _updateArmatureEnum() {
        const armatureEnum = this._getArmatureEnumSource();
        this._enumArmatures = Enum({});
        Object.assign(this._enumArmatures, armatureEnum);
        Enum.update(this._enumArmatures);
        setEnumAttr(this, '_armatureIndex', this._enumArmatures);
    }

    // 每次編輯器讀取列舉時檢查來源是否有變化（動畫來源還會隨 armatureName 改變），
    // 避免只在名稱為空或 setter 執行時才刷新，導致載入時名稱已經有效的實例一直停留在快取的 <None>。
    private _syncInspectorEnums() {
        if (!hasSameEnumEntries(this._getAnimEnumSource(), this._enumAnimations)) {
            this._updateAnimEnum();
        }
        if (!hasSameEnumEntries(this._getArmatureEnumSource(), this._enumArmatures)) {
            this._updateArmatureEnum();
        }
    }
}
